import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, observable, runInAction } from 'mobx';

import type { Result } from '@src/services/common/Result';
import type { DistributionChannelService } from '@src/services/DistributionChannelService';
import type { DistributionChannel } from '@src/types.ts/distribution-channel';

const logger = LogManager.getLogger('stores:DistributionChannels');

type EntryStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface DistributionChannelsState {
  status: EntryStatus;
  channels: DistributionChannel[];
  error: string | null;
}

interface Slice extends DistributionChannelsState {
  inFlight: Promise<void> | null;
}

interface Entry {
  enabled: Slice;
  available: Slice;
}

const emptySlice = (): Slice => ({
  status: 'idle',
  channels: [],
  error: null,
  inFlight: null,
});

const emptyEntry = (): Entry => ({
  enabled: emptySlice(),
  available: emptySlice(),
});

export class DistributionChannelsStore {
  // Observable map of showcaseId -> entry. Entries are replaced wholesale
  // (observable.ref-style) so observers re-render when status/channels flip.
  private entries = observable.map<string, Entry>(undefined, { deep: false });

  constructor(private service: DistributionChannelService) {
    makeAutoObservable<this, 'service'>(this, {
      service: false,
    });
  }

  getState(showcaseId: string): DistributionChannelsState {
    const entry = this.entries.get(showcaseId) ?? emptyEntry();
    return sliceToState(entry.enabled);
  }

  getAvailableState(showcaseId: string): DistributionChannelsState {
    const entry = this.entries.get(showcaseId) ?? emptyEntry();
    return sliceToState(entry.available);
  }

  loadForShowcase(
    showcaseId: string,
    options: { force?: boolean } = {},
  ): Promise<void> {
    return this.loadSlice(
      showcaseId,
      'enabled',
      () => this.service.findEnabledForShowcase(showcaseId),
      "Couldn't load distribution channels.",
      'Failed to load enabled distribution channels',
      options,
    );
  }

  loadAvailableForShowcase(
    showcaseId: string,
    options: { force?: boolean } = {},
  ): Promise<void> {
    return this.loadSlice(
      showcaseId,
      'available',
      () => this.service.findAvailableForShowcase(showcaseId),
      "Couldn't load available channels.",
      'Failed to load available distribution channels',
      options,
    );
  }

  private loadSlice(
    showcaseId: string,
    sliceKey: keyof Entry,
    fetcher: () => Promise<Result<DistributionChannel[]>>,
    userErrorMessage: string,
    logErrorMessage: string,
    options: { force?: boolean },
  ): Promise<void> {
    const existing = this.entries.get(showcaseId) ?? emptyEntry();
    const slice = existing[sliceKey];
    if (!options.force) {
      if (slice.status === 'ready') return Promise.resolve();
      if (slice.inFlight) return slice.inFlight;
    }

    const promise = fetcher().then(result => {
      runInAction(() => {
        const current = this.entries.get(showcaseId) ?? emptyEntry();
        if (result.ok) {
          this.entries.set(showcaseId, {
            ...current,
            [sliceKey]: {
              status: 'ready',
              channels: result.value,
              error: null,
              inFlight: null,
            },
          });
        } else {
          logger.warn(logErrorMessage, result.error);
          this.entries.set(showcaseId, {
            ...current,
            [sliceKey]: {
              status: 'error',
              channels: current[sliceKey].channels,
              error: userErrorMessage,
              inFlight: null,
            },
          });
        }
      });
    });

    this.entries.set(showcaseId, {
      ...existing,
      [sliceKey]: {
        ...existing[sliceKey],
        status: 'loading',
        error: null,
        inFlight: promise,
      },
    });

    return promise;
  }

  async enableChannel(
    showcaseId: string,
    channel: DistributionChannel,
  ): Promise<Result<void>> {
    const result = await this.service.enableChannelForShowcase(
      showcaseId,
      channel.id,
    );
    if (!result.ok) return result;

    // Apply local truth directly — no background refetch. The Content
    // Delivery GraphQL API has a brief lag behind CMA writes; refetching
    // immediately would race and risk flipping back to the pre-write state.
    runInAction(() => {
      const current = this.entries.get(showcaseId) ?? emptyEntry();
      const already = current.enabled.channels.some(c => c.id === channel.id);
      if (already) return;
      this.entries.set(showcaseId, {
        ...current,
        enabled: {
          ...current.enabled,
          channels: [...current.enabled.channels, channel],
          status: 'ready',
          inFlight: null,
        },
      });
    });
    return result;
  }

  async disableChannel(
    showcaseId: string,
    channelId: string,
  ): Promise<Result<void>> {
    const result = await this.service.disableChannelForShowcase(
      showcaseId,
      channelId,
    );
    if (!result.ok) return result;

    runInAction(() => {
      const current = this.entries.get(showcaseId) ?? emptyEntry();
      this.entries.set(showcaseId, {
        ...current,
        enabled: {
          ...current.enabled,
          channels: current.enabled.channels.filter(c => c.id !== channelId),
          status: 'ready',
          inFlight: null,
        },
      });
    });
    return result;
  }

  invalidate(showcaseId?: string): void {
    if (showcaseId) {
      this.entries.delete(showcaseId);
    } else {
      this.entries.clear();
    }
  }
}

const sliceToState = (slice: Slice): DistributionChannelsState => ({
  status: slice.status,
  channels: slice.channels,
  error: slice.error,
});
